import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormField } from 'components/ui/form';
import { InputGs, SearchableSelectGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useErrorHandler } from 'exceptions/ErrorHandler';
import { useCompany } from 'hooks/api/options';
import { useHubChecklistUser } from 'hooks/api';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { IHubChecklistUser } from 'interfaces/Hub';
import { formSchema } from '../../formUpdateSchema';

type UpdateFormProps = {
  data?: IHubChecklistUser;
};

export const UpdateForm: React.FC<UpdateFormProps> = ({ data }) => {
  const navigate = useHistory();
  const queryClient = useQueryClient();
  const { errorHandler } = useErrorHandler();
  const { updateChecklistUser } = useHubChecklistUser();
  const { getListCompany } = useCompany();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: data,
  });

  const mutation = useMutation(updateChecklistUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('updateChecklistUser');
      navigate.push('/settings/hub-checklist-user');
      toast.success('Usuário atualizado com sucesso!');
    },
    onError: (error) => {
      errorHandler(error);
    },
  });

  const {
    data: optionsCompany,
    isLoading: isLoadingOptionsCompany,
    refetch: refetchOptionsCompany,
  } = useQuery({
    queryKey: ['getListCompany'],
    queryFn: () => getListCompany({ all: true, type: 'CD', picking_manage: 1 }),
    enabled: false,
    onSuccess: (response: any) => {
    },
  });

  const onSubmit = (dataForm: z.infer<typeof formSchema>) => {
    mutation.mutate({
      ...dataForm,
      id: data?.id as number,
    });
  };

  useEffect(() => {
    if (data) {
      form.reset(data);
    }
  }, [data, form]);

  useEffect(() => {
    if (!optionsCompany && data) {
      refetchOptionsCompany();
    }
  }, [refetchOptionsCompany, optionsCompany, data]);

  useEffect(() => {
    if (data && data.userCompany && optionsCompany) {
      const findCompany = optionsCompany.find(
        (el: any) => el.value === String(data.userCompany.company_id),
      );

      if (findCompany) {
        form.setValue('general_company_id', findCompany.value);
      }
    }
  }, [optionsCompany, data]);

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <FormField
              name="name"
              control={form.control}
              render={({ field }) => <InputGs {...field} label="Nome" />}
            />
            <FormField
              name="general_company_id"
              control={form.control}
              render={({ field }) => (
                <SearchableSelectGs
                  {...field}
                  label={'Centro'}
                  onValueChange={(value: string) => {
                    field.onChange(value);
                  }}
                  content={optionsCompany || []}
                  isLoading={isLoadingOptionsCompany}
                  value={field.value ?? undefined}
                  disabled={isLoadingOptionsCompany}
                />
              )}
            />

            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <InputGs
                  {...field}
                  value={field.value ?? ''}
                  label="Email"
                  disabled
                />
              )}
            />
          </div>
          <div className="flex justify-end w-full gap-4">
            <Button.Root
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => navigate.push('/settings/hub-checklist-user')}
            >
              Cancelar
            </Button.Root>
            <Button.Root
              variant="primary"
              size="sm"
              type="submit"
              disabled={mutation.isLoading}
            >
              Salvar
            </Button.Root>
          </div>
        </form>
      </Form>
    </div>
  );
};
